/* global React */
/* eslint-disable no-nested-ternary,no-restricted-globals */
import { useState, Ref } from 'react'
import { Box } from '@mui/material'
import { getThemeColor, isObj, type PlatformEvent } from 'lib/util'
import { useMemoRef, useStyles } from 'platform/react/hook'
import useImage from 'platform/react/hook/useImage'
import ErrorBoundary from 'ui/Error'
import Picture from 'ui/Element/Image/Picture'
import Placeholder from 'ui/Element/Image/Placeholder'
import { type Attachment } from '@platform/react/hook/useAttachmentAPI'
import Icon from 'ui/Element/Icon'
import PlaceholderSimple from 'ui/Element/Placeholder/Simple'
import { type View } from 'react-native'

// Shape of the image container:
// - `natural`: the container follows the aspect ratio of the loaded image
// - `square`: the container is forced to a 1:1 aspect ratio
// - `circle`: like `square`, but clipped to a circle
export type ImageShape = 'natural' | 'square' | 'circle'

// How the image is fitted into its container whenever their aspect ratios
// differ. `contain` letterboxes the image, `cover` crops it. In both cases
// the image is centered and never overflows the container.
export type ImageFit = 'contain' | 'cover'

// Lifecycle of the attachment behind the image
export type ImageStatus = 'idle' | 'loading' | 'loaded' | 'error'

// What the Image component should currently render
export type ImageMode = 'loading' | 'picture' | 'fallbackPicture' | 'fallbackIcon' | 'placeholder' | 'empty'

// Mirrors the props of the Icon element. `background` is applied to the
// Image container instead of the icon itself, so that it fills the whole box.
export interface ImageIcon {
  /** the name of the icon */
  name?: string
  /** the icon source, or a nested icon definition */
  source?: string | ImageIcon
  /** the size of the icon */
  size?: number | string
  /** the color of the icon */
  color?: string
  /** background color applied to the image container */
  background?: string
  /** padding around the icon */
  padding?: number | string
  /** angle to rotate the icon */
  rotate?: number | string
}

// State the Image component reports to its parent via `events.onState`.
// This payload is identical on both platforms.
export interface ImageState {
  /** the lifecycle status of the attachment */
  status: ImageStatus
  /** the fetch progress, as a percentage */
  progress: number
  /** the resolved image source */
  src: string | null
  /** the attachment's file name */
  name: string | null
  /** triggers fetching the deferred attachment */
  fetch: () => void
}

export interface ImageEvents {
  /** called whenever the image reports new state */
  onState?: (state: ImageState) => void
  /** called once the image has loaded, with its size */
  onLoad?: (size: { width: number, height: number }) => void
  /** called when the image is clicked */
  onClick?: (event: unknown) => void
  /**
   * Legacy transform executed against the incoming attachment before it is
   * fetched. Kept for configuration-driven usages (e.g. `trimAttachmentName`).
   */
  onAttachment?: (event: PlatformEvent<{ value: Attachment | null }>) => Attachment | null
}

export interface ImageProps {
  // source
  /** the attachment to display */
  attachment?: Attachment | null
  /** the attachment value or key to display */
  value?: string | { key: string } | null
  /** the attachment api overrides */
  api?: Record<string, unknown>
  /** the attachment group */
  group?: string | null
  /** a file source that takes precedence over `attachment` */
  file?: string | null
  /** don't fetch the attachment until `fetch()` is called */
  defer?: boolean
  // layout
  /** the width of the container */
  width?: number | string | null
  /** the height of the container */
  height?: number | string | null
  /** the minimum width of the container */
  minWidth?: number | string | null
  /** the minimum height of the container */
  minHeight?: number | string | null
  /** the maximum width of the container */
  maxWidth?: number | string | null
  /** the maximum height of the container */
  maxHeight?: number | string | null
  /** the shape of the container */
  shape?: ImageShape
  /** how the image is fitted into its container */
  fit?: ImageFit
  /** rounded corners on the container (ignored for `circle`) */
  rounded?: boolean
  /** whether the container has a border */
  bordered?: boolean
  /** background color of the container */
  background?: string | null
  // fallbacks
  /** icon shown when there is no image */
  fallbackIcon?: ImageIcon | string | null
  /** picture shown when there is no image */
  fallbackPicture?: string | null
  /** whether a file-type placeholder may be shown; an object enables and styles its icon */
  placeholder?: boolean | Omit<ImageIcon, 'name' | 'source'>
  /** whether to show a loading skeleton */
  skeleton?: boolean
  /** the file type map used to pick placeholders */
  types?: Record<string, string[]>
  /** the image's alt text */
  alt?: string
  /** the image's event handlers */
  events?: ImageEvents
  /** a reference to the root element */
  ref?: Ref<View>
}

export interface PictureEvents {
  /** called once the picture has loaded, with its size */
  onLoad?: (size: { width: number, height: number }) => void
}

export interface PictureProps {
  /** the image source */
  src: string
  /** the image's alt text (web only) */
  alt?: string
  /** how the image is fitted into its container */
  fit?: ImageFit
  /** when shape={'circle'} is used, prevents the corners from being clipped off */
  inset?: boolean
  /** the picture's event handlers */
  events?: PictureEvents
}

// Props of the platform Placeholder elements
export interface PlaceholderProps {
  /** filename to derive the placeholder from */
  name?: string | null
  /** the file type map used to pick the placeholder */
  types?: Record<string, string[]>
  /** styling applied to the file icon */
  iconProps?: Omit<ImageIcon, 'name' | 'source'>
}

export interface ImageStyleProps {
  /** the width of the container */
  width?: number | string | null
  /** the height of the container */
  height?: number | string | null
  /** the minimum width of the container */
  minWidth?: number | string | null
  /** the minimum height of the container */
  minHeight?: number | string | null
  /** the maximum width of the container */
  maxWidth?: number | string | null
  /** the maximum height of the container */
  maxHeight?: number | string | null
  /** the shape of the container */
  shape: ImageShape
  /** rounded corners on the container */
  rounded?: boolean
  /** whether the container has a border */
  bordered?: boolean,
  /** what the image currently renders */
  mode?: ImageMode,
  /** the aspect ratio of the container */
  ratio: number | null
  /** background color of the container */
  background?: string | null
  /** whether the image is clickable (web only) */
  clickable?: boolean
}

const FILE_PROTOCOL = /^file:\/\//
const REMOTE_PROTOCOL = /^https?:\/\//

/**
 * Resolves a local asset name to the url it is served under.
 *
 * Paths starting with `http(s)://` are passed through as is.
 * Paths starting with `file://...` are passed with the protocol stripped (just ...).
 * Paths ending in `.svg` are resolved in `assets/icon`.
 * All other paths are resolved in `assets/img`.
 *
 * @param name a local asset name, `file://` path or absolute url
 * @returns    the url of the asset
 */
const resolveAsset = (
  name: string,
) : string => (FILE_PROTOCOL.test(name) && name.replace(FILE_PROTOCOL, ''))
           || (REMOTE_PROTOCOL.test(name) && name)
           || `${name.includes('svg') ? 'assets/icon' : 'assets/img'}/${name}`

/**
 * Returns the image element's current theme styles.
 *
 * The container implements the element's layout rules:
 * - without constraints it stretches in both directions, filling all the
 *   available space
 * - with a constraint (`width` and/or `height`) it stretches until the
 *   constraint is reached, preserving the image's aspect ratio
 * - it never overflows its parent container; anything bigger is clipped and
 *   the image itself stays centered
 *
 * @param theme            the application's current theme
 * @param props            the element's properties
 * @param props.width      optional fixed width of the container
 * @param props.height     optional fixed height of the container
 * @param props.minWidth   optional minimum width of the container
 * @param props.minHeight  optional minimum height of the container
 * @param props.maxWidth   optional maximum width of the container
 * @param props.maxHeight  optional maximum height of the container
 * @param props.shape      shape of the container
 * @param props.ratio      natural aspect ratio of the loaded image
 * @param props.background background color behind the fallback icon
 * @param props.clickable  whether the container reacts to clicks
 * @returns                the image's styles
 */
const styles = (theme: any, {
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  shape,
  rounded,
  ratio,
  background,
  clickable,
  bordered,
  mode,
}: ImageStyleProps) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    ...!width && !height && {
      flex: 1,
    },
    ...bordered && (mode === 'picture' || mode === 'fallbackPicture') && {
      border: `1px solid ${theme.palette.gray[900]}`,
    },
    width: width ?? '100%',
    height: height ?? '100%',
    ...minWidth && { minWidth },
    ...minHeight && { minHeight },
    maxWidth: maxWidth ?? '100%',
    maxHeight: maxHeight ?? '100%',
    ...background && {
      backgroundColor: getThemeColor(theme, background),
    },
    ...shape === 'circle' && {
      borderRadius: '50%',
    },
    ...rounded && shape !== 'circle' && {
      borderRadius: '0.5rem',
    },
    ...(shape === 'circle' || shape === 'square') && {
      aspectRatio: '1 / 1',
    },
    ...shape === 'natural' && ratio && {
      aspectRatio: ratio,
    },
    ...clickable && {
      cursor: 'pointer',
    },
  },
  ...theme.custom.image,
})

/**
 * Image Element
 *
 * Displays an image from an attachment, a resource reference or a local file
 * and decides itself what to render at any moment: the image, a loading
 * skeleton, a fallback icon or a file-type placeholder.
 *
 * @param props                  the element's properties
 * @param props.ref              a reference to the root element
 * @param props.attachment       attachment to fetch and display
 * @param props.value            key (or object with key) of a resource to fetch
 * @param props.api              api information for the resource to fetch
 * @param props.group            attachment group of the attachment
 * @param props.file             local asset name, `file://` path or absolute url, wins over
 *                               `attachment`
 * @param props.defer            don't fetch the attachment until `fetch()` is called
 * @param props.width            optional fixed width of the image
 * @param props.height           optional fixed height of the image
 * @param props.minWidth         optional minimum width of the image
 * @param props.minHeight        optional minimum height of the image
 * @param props.maxWidth         optional maximum width of the image
 * @param props.maxHeight        optional maximum height of the image
 * @param props.shape            shape of the image container
 * @param props.fit              how the image is fitted into its container
 * @param props.fallbackIcon     icon to fall back to when nothing can be displayed
 * @param props.fallbackPicture  image asset to fall back to when nothing can be displayed
 * @param props.placeholder      whether a file-type placeholder may be shown
 * @param props.skeleton         whether a skeleton is shown while loading
 * @param props.types            file type map used to derive the placeholder
 * @param props.alt              alternative text of the image
 * @param props.events           event handlers (`onState`, `onLoad`, `onClick`, `onAttachment`)
 * @returns                      the new image element
 */
const Image = ({ ref, ...props }: ImageProps) => {
  const {
    file = null,
    fallbackPicture = null,
    width = null,
    height = null,
    minWidth = null,
    minHeight = null,
    maxWidth = null,
    maxHeight = null,
    shape = 'natural' as ImageShape,
    fit = 'contain' as ImageFit,
    rounded = false,
    bordered = false,
    background: rootBackground = null,
    fallbackIcon = null,
    skeleton = true,
    types,
    alt,
    events = {},
  } = props

  const { mode, src, name, onLoad } = useImage(props)
  const [ratio, setRatio] = useState<number | null>(null)

  const fallbackIconIsObject = isObj(fallbackIcon)
  const { background = null, ...fallbackIconProps } = fallbackIconIsObject
    ? fallbackIcon as ImageIcon
    : {}

  const {
    background: placeholderBackground = null,
    ...placeholderIconProps
  } = (isObj(props.placeholder)
    ? props.placeholder as object
    : {}) as ImageIcon

  const classes = useStyles(styles, {
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    shape,
    rounded,
    bordered,
    mode,
    ratio: shape === 'natural' ? ratio : null,
    background: (mode === 'fallbackIcon' && background)
      || (mode === 'placeholder' && placeholderBackground)
      || rootBackground,
    clickable: !!events?.onClick,
  })() as Record<string, string>

  /**
   * Stores the image's natural ratio, so that the container can follow it,
   * notifies the attachment adapter and the parent component.
   *
   * @param size the natural dimensions of the loaded image
   */
  const handleLoad = (size: { width: number, height: number }) => {
    size.width && size.height && setRatio(size.width / size.height)
    onLoad?.()
    events?.onLoad?.(size)
  }

  const source = file ? resolveAsset(file) : src

  // if shape={'circle'} is used, image corners will be clipped of
  // this prevents that by insetting the image slightly
  const inset = shape === 'circle' && fit === 'contain'

  return (
    <ErrorBoundary>
      <Box
        ref={ref}
        className={classes.root}
        {...events?.onClick && { onClick: events.onClick }}
      >
        {mode === 'loading' && skeleton && (
          <PlaceholderSimple
            hidden={true}
            timeout={500}
            placeholderProps={{
              height: '100%',
              ...shape === 'circle' && { variant: 'circular' },
            }}
          />
        )}
        {mode === 'picture' && source && (
          <Picture
            src={source}
            alt={alt ?? name ?? (file || '')}
            fit={fit}
            inset={inset}
            events={{ onLoad: handleLoad }}
          />
        )}
        {mode === 'fallbackPicture' && (
          <Picture
            src={resolveAsset(fallbackPicture as string)}
            alt={alt ?? fallbackPicture as string}
            fit={fit}
            inset={inset}
            events={{ onLoad: handleLoad }}
          />
        )}
        {mode === 'fallbackIcon' && (
          <Icon source={fallbackIconIsObject ? fallbackIconProps : fallbackIcon} />
        )}
        {mode === 'placeholder' && (
          <Placeholder
            name={name}
            types={types}
            iconProps={placeholderIconProps}
          />
        )}
      </Box>
    </ErrorBoundary>
  )
}

export default useMemoRef(Image, (props: ImageProps) => [
  props.file,
  props.group,
  props.attachment,
  props.value,
])
